import React, { useState, useEffect } from 'react'
import { Steps, Card } from 'antd'
import VerifyToken from './VerifyToken';
import ProjectInformation from './ProjectInformation';
import AdditionalInfo from './AdditionalInfo';
import Review from './Review/Review';
import {
  getERC20TokenDataByTokenAddress,
  getERC20TokenBalanceByWalletAddress
} from '../../Blockchain/services/common.service';
import './Launchpad.css'
import { useTranslation } from 'react-i18next';
import { useAccount } from 'wagmi';

function CreatePresale() {

  const { address: account } = useAccount();
  const { t } = useTranslation();

  const { Step } = Steps;
  const [stepNumber, setStepNumber] = useState(0)
  const [isNextButtonActive, setIsNextButtonActive] = useState(false)

  // verifyToken variables
  const [tokenAddress, setTokenAddress] = useState('')
  const [tokenValidationStatus, setTokenValidationStatus] = useState(null)
  const [tokenValidationHelperMessage, setTokenValidationHelperMessage] = useState(null)
  const [isTokenDetailsLoading, setIsTokenDetailsLoading] = useState(false)
  const [tokenName, setTokenName] = useState('')
  const [tokenSymbol, setTokenSymbol] = useState('')
  const [userTokenBalance, setUserTokenBalance] = useState(0.00)
  const [tokenDecimals, setTokenDecimals] = useState('')
  const [totalSupply, setTotalSupply] = useState('')
  const [yourBalanceValidationStatus, setYourBalanceValidationStatus] = useState(null)
  const [yourBalanceValidationHelperMessage, setYourBalanceValidationHelperMessage] = useState(null)
  const [liquidityToken, setLiquidityToken] = useState('BNB')
  const [liquidityTokenName, setLiquidityTokenName] = useState('BNB')

  // ProjectInformation variables
  const [projectName, setProjectName] = useState('')
  const [tokensPerBNB, setTokensPerBNB] = useState('')
  const [minimumBuy, setMinimumBuy] = useState('')
  const [maximumBuy, setMaximumBuy] = useState('')
  const [hardCap, setHardCap] = useState('')
  const [startTime, setStartTime] = useState(null)
  const [endTime, setEndTime] = useState(null)
  const [isWhitelistingEnabled, setIsWhitelistingEnabled] = useState(false)
  const [publicStartTime, setPublicStartTime] = useState(null)
  const [softCap, setSoftCap] = useState('')
  const [isVestingEnabled, setIsVestingEnabled] = useState(false)
  const [initialTokenReleasePercentage, setInitialTokenReleasePercentage] = useState('')
  const [vestingCyclesInDays, setVestingCyclesInDays] = useState('')
  const [tokenReleasePercentageInCycle, setTokenReleasePercentageInCycle] = useState('')
  const [liquidityPercentage, setLiquidityPercentage] = useState('')
  const [liquidityUnlockTime, setLiquidityUnlockTime] = useState('')
  const [launchRate, setLaunchRate] = useState('')
  const [liquidityProvider, setLiquidityProvider] = useState(process.env.REACT_APP_PCS_ROUTER_ADDRESS)
  const [totalTokenNeeded, setTotalTokenNeeded] = useState('')

  // AdditionalInfo variables
  const [logoURL, setLogoURL] = useState('')
  const [coverImageUrl, setCoverImageUrl] = useState('')
  const [website, setWebsite] = useState('')
  const [facebookLink, setFacebookLink] = useState('')
  const [twitterLink, setTwitterLink] = useState('')
  const [githubLink, setGithubLink] = useState('')
  const [telegramLink, setTelegramLink] = useState('')
  const [instagramLink, setInstagramLink] = useState('')
  const [discordLink, setDiscordLink] = useState('')
  const [redditLink, setRedditLink] = useState('')
  const [description, setDescription] = useState('')
  const [youtubeLink, setYoutubeLink] = useState('')
  const [kycLink, setKycLink] = useState('')
  const [auditedLink, setAuditedLink] = useState('')
  const [safuLink, setSafuLink] = useState('')
  const [poocoinLink, setPoocoinLink] = useState('')

  const [isLiquidityBurn, setIsLiquidityBurn] = useState(false)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])


  // verifyToken functions
  const fetchUserTokenBalance = async () => {
    try {

      if (account) {
        setYourBalanceValidationStatus('validating')
        setYourBalanceValidationHelperMessage(t('Fetching token balance please wait'))
        const tokenBalanceResponse = await getERC20TokenBalanceByWalletAddress(tokenAddress, account)
        setUserTokenBalance(tokenBalanceResponse)
        setYourBalanceValidationStatus(null)
        setYourBalanceValidationHelperMessage(null)
      } else {
        setYourBalanceValidationHelperMessage(t('Please connect your wallet to fetch the token balance'))
        setUserTokenBalance(0)
        setYourBalanceValidationStatus('error')
      }

    } catch (error) {
      setYourBalanceValidationHelperMessage(null)
      setYourBalanceValidationStatus(null)
    }
  }

  const fetchTokenDetails = async () => {
    try {
      setIsTokenDetailsLoading(true)
      setTokenValidationStatus('validating')
      setTokenValidationHelperMessage(t('validating token data'))
      const tokenDataResponse = await getERC20TokenDataByTokenAddress(tokenAddress)

      setTokenValidationHelperMessage(null)
      setTokenValidationStatus('success')
      setIsTokenDetailsLoading(false)

      //set token details
      setTokenName(tokenDataResponse?.tokenName)

      const tokenTotalSupply = tokenDataResponse?.tokenTotalSupply
      const tokenDecimals = tokenDataResponse?.tokenDecimals

      const tokenTotalSupplyValue = tokenTotalSupply / 10 ** tokenDecimals

      setTotalSupply(tokenTotalSupplyValue)
      setTokenSymbol(tokenDataResponse?.tokenSymbol)
      setTokenDecimals(tokenDataResponse?.tokenDecimals)

    } catch (error) {
      setTokenValidationStatus('error')
      setTokenValidationHelperMessage(error?.message)

      //set token details
      setTokenName('')
      setTotalSupply('')
      setTokenSymbol('')
      setTokenDecimals('')
      setUserTokenBalance('')
      setIsTokenDetailsLoading(false)
    }
  }

  useEffect(() => {

    if (tokenAddress) {
      fetchUserTokenBalance()
    } else {
      setUserTokenBalance('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenAddress, account])

  useEffect(() => {
    if (tokenAddress) {
      fetchTokenDetails()
    } else {
      setTokenValidationStatus(null)
      setTokenValidationHelperMessage(null)
      setTokenName('')
      setTotalSupply('')
      setTokenSymbol('')
      setTokenDecimals('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenAddress])

  return (
    <div className='mb-5'>
      {/* <h2 className='text-center mt-4 mb-4 primary-text'>Launchpad</h2> */}

      <Steps current={stepNumber}>
        <Step title={t("Verify Token")} description={t("Enter the token address and connect your wallet.")} />
        <Step title={t("Project Information")} description={t("Enter detailed information relating to your presale.")} />
        <Step title={t("Additional Information")} description={t("Provide more information to the public.")} />
        <Step title={t("Review")} description={t("Review your submitted information and submit.")} />
      </Steps>

      <Card className='kingsale-card-bg mt-5'>
        {
          stepNumber === 0 &&
          <VerifyToken
            setStepNumberParent={setStepNumber}
            setTokenAddress={setTokenAddress}
            tokenAddress={tokenAddress}
            tokenValidationStatus={tokenValidationStatus}
            tokenValidationHelperMessage={tokenValidationHelperMessage}
            isTokenDetailsLoading={isTokenDetailsLoading}
            tokenName={tokenName}
            tokenSymbol={tokenSymbol}
            userTokenBalance={userTokenBalance}
            tokenDecimals={tokenDecimals}
            totalSupply={totalSupply}
            liquidityToken={liquidityToken}
            setLiquidityToken={setLiquidityToken}
            yourBalanceValidationStatus={yourBalanceValidationStatus}
            yourBalanceValidationHelperMessage={yourBalanceValidationHelperMessage}
            setIsNextButtonActive={setIsNextButtonActive}
            isNextButtonActive={isNextButtonActive}
            setLiquidityTokenName={setLiquidityTokenName}
          />
        }

        {
          stepNumber === 1 &&
          <ProjectInformation
            setStepNumberParent={setStepNumber}
            projectName={projectName}
            setProjectName={setProjectName}
            tokensPerBNB={tokensPerBNB}
            setTokensPerBNB={setTokensPerBNB}
            minimumBuy={minimumBuy}
            setMinimumBuy={setMinimumBuy}
            maximumBuy={maximumBuy}
            setMaximumBuy={setMaximumBuy}
            hardCap={hardCap}
            setHardCap={setHardCap}
            softCap={softCap}
            setSoftCap={setSoftCap}
            startTime={startTime}
            setStartTime={setStartTime}
            endTime={endTime}
            setEndTime={setEndTime}
            isNextButtonActive={isNextButtonActive}
            setIsNextButtonActive={setIsNextButtonActive}
            isWhitelistingEnabled={isWhitelistingEnabled}
            setIsWhitelistingEnabled={setIsWhitelistingEnabled}
            publicStartTime={publicStartTime}
            setPublicStartTime={setPublicStartTime}
            isVestingEnabled={isVestingEnabled}
            setIsVestingEnabled={setIsVestingEnabled}
            initialTokenReleasePercentage={initialTokenReleasePercentage}
            setInitialTokenReleasePercentage={setInitialTokenReleasePercentage}
            vestingCyclesInDays={vestingCyclesInDays}
            setVestingCyclesInDays={setVestingCyclesInDays}
            tokenReleasePercentageInCycle={tokenReleasePercentageInCycle}
            setTokenReleasePercentageInCycle={setTokenReleasePercentageInCycle}
            liquidityPercentage={liquidityPercentage}
            setLiquidityPercentage={setLiquidityPercentage}
            liquidityUnlockTime={liquidityUnlockTime}
            setLiquidityUnlockTime={setLiquidityUnlockTime}
            launchRate={launchRate}
            setLaunchRate={setLaunchRate}
            liquidityProvider={liquidityProvider}
            setLiquidityProvider={setLiquidityProvider}
            tokenSymbol={tokenSymbol}
            liquidityToken={liquidityToken}
            liquidityTokenName={liquidityTokenName}
            userTokenBalance={userTokenBalance}
            setTotalTokenNeeded={setTotalTokenNeeded}
            isLiquidityBurn={isLiquidityBurn}
            setIsLiquidityBurn={setIsLiquidityBurn}
          />
        }

        {
          stepNumber === 2 &&
          <AdditionalInfo
            setStepNumberParent={setStepNumber}
            logoURL={logoURL}
            setLogoURL={setLogoURL}
            coverImageUrl={coverImageUrl}
            setCoverImageUrl={setCoverImageUrl}
            website={website}
            setWebsite={setWebsite}
            facebookLink={facebookLink}
            setFacebookLink={setFacebookLink}
            twitterLink={twitterLink}
            setTwitterLink={setTwitterLink}
            githubLink={githubLink}
            setGithubLink={setGithubLink}
            telegramLink={telegramLink}
            setTelegramLink={setTelegramLink}
            instagramLink={instagramLink}
            setInstagramLink={setInstagramLink}
            discordLink={discordLink}
            setDiscordLink={setDiscordLink}
            redditLink={redditLink}
            setRedditLink={setRedditLink}
            description={description}
            setDescription={setDescription}
            youtubeLink={youtubeLink}
            setYoutubeLink={setYoutubeLink}
            kycLink={kycLink}
            setKycLink={setKycLink}
            auditedLink={auditedLink}
            setAuditedLink={setAuditedLink}
            safuLink={safuLink}
            setSafuLink={setSafuLink}
            poocoinLink={poocoinLink}
            setPoocoinLink={setPoocoinLink}
          />
        }

        {
          stepNumber === 3 &&
          <Review
            tokenAddress={tokenAddress}
            totalTokenNeeded={totalTokenNeeded}
            tokenSymbol={tokenSymbol}
            tokenName={tokenName}
            tokenDecimals={tokenDecimals}
            liquidityProvider={liquidityProvider}
            tokensPerBNB={tokensPerBNB}
            launchRate={launchRate}
            setStepNumberParent={setStepNumber}
            projectName={projectName}
            minimumBuy={minimumBuy}
            maximumBuy={maximumBuy}
            liquidityTokenName={liquidityTokenName}
            liquidityToken={liquidityToken}
            softCap={softCap}
            hardCap={hardCap}
            liquidityPercentage={liquidityPercentage}
            startTime={startTime}
            endTime={endTime}
            liquidityUnlockTime={liquidityUnlockTime}
            isWhitelistingEnabled={isWhitelistingEnabled}
            publicStartTime={publicStartTime}
            logoURL={logoURL}
            coverImageUrl={coverImageUrl}
            website={website}
            facebookLink={facebookLink}
            twitterLink={twitterLink}
            githubLink={githubLink}
            telegramLink={telegramLink}
            instagramLink={instagramLink}
            discordLink={discordLink}
            redditLink={redditLink}
            description={description}
            isVestingEnabled={isVestingEnabled}
            initialTokenReleasePercentage={initialTokenReleasePercentage}
            vestingCyclesInDays={vestingCyclesInDays}
            tokenReleasePercentageInCycle={tokenReleasePercentageInCycle}
            youtubeLink={youtubeLink}
            kycLink={kycLink}
            auditedLink={auditedLink}
            safuLink={safuLink}
            poocoinLink={poocoinLink}
            isLiquidityBurn={isLiquidityBurn}
          />
        }
      </Card>
    </div>
  )
}

export default CreatePresale